import {inject, Injectable} from '@angular/core';
import {combineLatest, firstValueFrom, lastValueFrom, Observable} from 'rxjs';
import {VaultStore} from '../stores/vault.store';
import {Vault} from '../api/models/vault';
import {map, skipWhile, take} from 'rxjs/operators';
import {TagGroupDefinitionWithIcon} from '../models/tag/tag-group-definition-with-icon.model';
import {EntityQueryService} from '../modules/akita-mimic/services/entity-query.service';
import {VaultState} from '../modules/akita-mimic/models/store-states/vault-state.model';
import {SortOrder} from '../modules/akita-mimic/enums/sort-order.enum';

@Injectable({ providedIn: 'root' })
export class VaultQuery extends EntityQueryService<VaultState, Vault> {
    public hasDataLoaded$: Observable<boolean> = this.select('hasDataLoaded');
    public isLoading$: Observable<boolean> = this.selectLoading();
    public vaults$: Observable<Array<Vault>> = this.selectAll();
    public activeVault$: Observable<Vault | undefined> = this.selectFirstActive();
    public activeVaultId$: Observable<string | undefined> = this.selectFirstActiveId()
        .pipe(map(id => id || undefined));
    public userVaults$: Observable<Array<Vault>>;
    public vaultsNotUsedByUser$: Observable<Array<Vault>>;

    public constructor() {
        const store = inject(VaultStore);
        super(store, {
            sortBy: 'id',
            sortByOrder: SortOrder.ASC
        });
        this.userVaults$ = this.select('userVaults')
            .pipe(
                map((vaultIds: Array<string>) => {
                    if (vaultIds && vaultIds.length > 0) {
                        return vaultIds.map(vaultId => {
                                return this.getVaultById(vaultId);
                            })
                            .filter(vault => vault !== undefined) as Vault[];
                    } else {
                        return [] as Array<Vault>;
                    }
                })
            );
        this.vaultsNotUsedByUser$ = combineLatest([
            this.userVaults$,
            this.selectAll()
        ])
            .pipe(
                map(([userVaults, vaults]: [Array<Vault>, Array<Vault>]) => {
                    return vaults.filter(vault => {
                        return (!userVaults.some(userVault => userVault.id === vault.id));
                    });
                }));
    }

    public getVaultNameById(vaultId: string): string {
        return this.getEntity(vaultId)?.name as string;
    }

    public getVaultById(vaultId: string): Vault | undefined {
        return this.getEntity(vaultId) as Vault;
    }

    public getFirstActiveVault(): Vault | undefined {
        return this.getFirstActive();
    }

    public getFirstActiveVaultId(): string | undefined {
        return this.getFirstActiveId();
    }

    public async waitForHasData(): Promise<boolean> {
        return firstValueFrom(this.vaults$
            .pipe(
                map(vaults => vaults.length > 0)
            ));
    }

    public async waitForHasDataLoaded(): Promise<void> {
        await lastValueFrom(this.hasDataLoaded$.pipe(skipWhile(hasDataLoaded => !hasDataLoaded), take(1)));
    }

    public getAmountOfTagGroupDefinitions(vaultId: string): number {
        return this.getValue().tagGroupDefinitions[vaultId].length;
    }

    public getTagGroupDefinitions(vaultId: string): Array<TagGroupDefinitionWithIcon> | undefined {
        return this.getValue().tagGroupDefinitions[vaultId];
    }

    public getIsVaultInStoreById(vaultId: string): boolean {
        return this.getVaultById(vaultId) !== undefined;
    }
}
